<template>
  <article
    v-if="product"
    class="product-item"
    @mouseover="mouseOver"
    @mouseout="mouseOut"
  >
    <div class="product-item__visual">
      <img
        class="product-item__image"
        :src="imageSrc"
        width="400"
        height="400"
        :loading="lazy ? 'lazy' : 'eager'"
        :fetchpriority="lazy ? 'low' : 'high'"
        :decoding="lazy ? 'async' : 'sync'"
        :alt="product.name || ''"
      >
      <div class="product-item__labels">
        <ProductLabelMarketing
          v-if="product.marketingLabel"
          :product="product"
        />
      </div>
    </div>

    <div class="product-item__actions">
      <ProductToggleWishlist
        class="product-item__wishlist"
        :product="product"
      />

      <ProductColorVariant
        v-if="product?.relatedColorProducts?.length"
        :color-variants="[{ url: product.url, color: product.color, stockStatus: product.stockStatus ?? true }, ...product.relatedColorProducts]"
        :active-url="product.url"
        class="product-item__color-variants"
      />
    </div>

    <div class="product-item__details">
      <span class="product-item__brand">
        {{ product?.brand?.label }}
      </span>

      <h3
        v-if="showDescription"
        class="product-item__description"
      >
        <component
          :is="getElement()"
          :to="`${product.url}${parameter}`"
          class="product-item__link"
          :class="{
            'sqzl-click': product?.squeezely,
          }"
          :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
          :data-sqzl-variant-id="product?.squeezely?.variantId"
          :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
          @click="handleLinkClick"
        >
          {{ product.name }}
        </component>
      </h3>

      <span
        v-if="isFreegift"
        class="label label--sale"
      >
        {{ $t('Free') }}
      </span>

      <ProductPrice
        v-if="showPrice && !isFreegift"
        class="product-item__price"
        :prices="{
          fromPrice: product.type === 'configurable' ? product.finalPrice : undefined,
          regularPrice: product.regularPrice,
          finalPrice: product.finalPrice,
        }"
        :show-vat-tag="false"
      />
    </div>

    <template v-if="showCta">
      <button
        v-if="product.type === 'simple'"
        class="product-item__button button button--shop-flow button--small"
        type="button"
        :disabled="pending"
        @click="add"
      >
        <IconPlus class="icon icon--cart" />
        <span>{{ $t('Add') }}</span>
      </button>

      <AppLink
        v-else
        :to="`${product.url}${parameter}`"
        class="product-item__button button button--shop-flow button--small"
      >
        <span>{{ $t('View product') }}</span>
        <IconArrow class="icon icon--arrow-right" />
      </AppLink>
    </template>

    <slot name="compare">
      <ProductToggleCompareProductList
        v-if="showCompare"
        class="product-item__compare-list"
        :product="product"
      />
    </slot>

    <slot />
  </article>
</template>

<script lang="ts" setup>
import type { EsProduct } from '~/types/elastic'
import type { DataLayerItem } from '~/composables/useDataLayer'
import { AppLink } from '#components'

const props = withDefaults(
  defineProps<{
    product: EsProduct
    lazy: boolean
    list?: string
    parameter?: string
    dataLayerOverrides?: Partial<DataLayerItem>
    linkable?: boolean
    showDescription?: boolean
    showPrice?: boolean
    showCta?: boolean
    showCompare?: boolean
    itemPrice?: number
  }>(),
  {
    list: 'other',
    parameter: '',
    dataLayerOverrides: undefined,
    linkable: true,
    showDescription: true,
    showPrice: true,
    showCta: false,
    showCompare: false,
  },
)

const { product } = toRefs(props)

const isFreegift = computed(() => {
  const finalPrice = props.itemPrice ?? product.value.finalPrice ?? 0
  return finalPrice < 0.01
})

const hover = ref(false)

const imageSrc = computed(() => {
  let imageSrc = product.value.image

  if (hover.value && product.value.hover_image) {
    imageSrc = product.value.hover_image
  }
  return getResizedProductImage(
    imageSrc,
    product.value.name,
    {
      width: 400,
      height: 400,
      imageSize: product.value.imageSize,
      index: 1,
    },
  )
})

function getElement() {
  return product.value.url && props.linkable ? AppLink : 'div'
}

function mouseOver() {
  hover.value = true
}

function mouseOut() {
  hover.value = false
}

const emit = defineEmits(['productClick'])
const { selectItem } = useDataLayer()
const { lastRequestId, sessionKey, profileKey, track: trackTweakwise } = useTweakwise()

const trackTweakwiseItemClick = (product: any) => {
  trackTweakwise('itemclick', {
    RequestId: lastRequestId.value,
    ItemId: product.tweakwiseId,
    ProfileKey: profileKey.value,
    SessionKey: sessionKey.value,
  })
}

const trackProductClick = (
  { product, dataLayerOverrides }:
  { product: EsProduct, dataLayerOverrides?: Partial<DataLayerItem> },
) => {
  selectItem(product, dataLayerOverrides)
  emit('productClick', product)
}

const handleLinkClick = () => {
  trackProductClick({ product: product.value, dataLayerOverrides: props.dataLayerOverrides })
  trackTweakwiseItemClick(product.value)

  if (product.value?.squeezely && product.value.url) {
    navigateTo(`${product.value.url}${props.parameter}`)
  }
}

const { addToCart } = useAddToCart()
const pending = ref(false)

const add = async () => {
  pending.value = true

  await addToCart([{
    quantity: 1,
    sku: product.value.sku,
  }], { product: props.product, suppress: false })

  pending.value = false
}
</script>

<style>
@import url("~layers/app/components/Product/Tile.css");
</style>
